/* eslint-disable indent */
import { isUserInSessionOrRoleSuperAdmin } from '@src/main/modules/auth/authUtils';
import { createExportHandlers } from '@src/main/modules/messages/utils/createExportHandlers';
import { normalizeUser } from '@src/main/modules/messages/utils/formatters';
import { useRoles } from '@src/main/modules/roles/ui/hooks/useRoles';
import { CreateUpdateUserRequest } from '@src/main/modules/user/infrastructure/dto/createUpdateUserRequest';
import UserForm from '@src/main/modules/user/ui/components/UserForm';
import { useCreateUser } from '@src/main/modules/user/ui/hooks/useCreateUser';
import { useDeleteUser } from '@src/main/modules/user/ui/hooks/useDeleteUser';
import { useUpdateUser } from '@src/main/modules/user/ui/hooks/useUpdateUser';
import { useUsers } from '@src/main/modules/user/ui/hooks/useUsers';
import { UserTableRow } from '@src/main/modules/user/ui/user-table-row.model';
import { getUserSchema } from '@src/main/modules/user/ui/user.schema';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTable from '@src/main/shared/ui/components/table/AppTable';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { formatList } from '@src/main/shared/utils/formatList';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const UsersManagement: FC = () => {
  const { t } = useTranslation();
  const { hasPermission, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data = [], isLoading: loadingUsers, repository } = useUsers();
  const { data: availableRoles = [], isLoading: loadingRoles } = useRoles();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEditUser, setNewEditUser] = useState<CreateUpdateUserRequest>({
    employeeId: '',
    fullName: '',
    roles: [],
  });
  const hasEditPermissions = hasPermission(PERMISSIONS.USERS_MANAGEMENT.edit);
  const [selectedUsers, setSelectedUsers] = useState<UserTableRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserTableRow | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const toolbar = [false, hasEditPermissions, selectedUsers.length > 0, true, false, false];

  const usersData: UserTableRow[] = data.map((user) => ({
    id: user.employeeId!,
    ...user,
    rolesDesc: user.roles.join(',\r\n '),
  }));

  const FILE_NAME = t('usersManagement.fileName');
  const { onExportExcel, onExportPdf } = createExportHandlers(
    usersData,
    normalizeUser,
    FILE_NAME,
    t,
  );

  const normalizeEmployeeId = (id: string) => id.trim().toLowerCase().replace(/\s+/g, '-');

  const { mutateAsync: createUser } = useCreateUser();
  const { mutateAsync: updateUser } = useUpdateUser();
  const { mutateAsync: deleteUser } = useDeleteUser();

  const handleShowCreateUser = () => {
    setNewEditUser({
      employeeId: '',
      fullName: '',
      roles: [],
    });
    setIsEditMode(false);
    setIsModalVisible(true);
  };

  const handleEditUser = (row: UserTableRow) => {
    const user: CreateUpdateUserRequest = {
      employeeId: row.employeeId,
      fullName: row.fullName,
      roles: row.roles,
    };
    setNewEditUser(user);
    setIsEditMode(true);
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    if (!repository) return;
    const schema = getUserSchema(t);

    const roleIds = availableRoles
      .filter((r) => newEditUser.roles.includes(r.role))
      .map((r) => r.roleId);

    try {
      const normalizedUser: CreateUpdateUserRequest = {
        ...newEditUser,
        employeeId: isEditMode
          ? newEditUser.employeeId
          : normalizeEmployeeId(newEditUser.employeeId),
        roles: roleIds,
      };
      schema.parse(normalizedUser);

      if (isEditMode) {
        const updateRequest: CreateUpdateUserRequest = {
          ...normalizedUser,
          employeeId: normalizedUser.employeeId,
        };
        await updateUser(updateRequest);
        showSuccessMessage(toast, t('usersManagement.updatedSuccess'));
      } else {
        await createUser(normalizedUser);
        showSuccessMessage(toast, t('usersManagement.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['user'] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            const key = err.path[0];
            errors[key] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const handleRoleChange = (role: string, e: CheckboxChangeEvent) => {
    const updatedRoles = e.checked
      ? [...newEditUser.roles, role]
      : newEditUser.roles.filter((r) => r !== role);
    setNewEditUser({ ...newEditUser, roles: updatedRoles });
  };

  const handleDeleteUser = (user: UserTableRow) => {
    setUserToDelete(user);
  };

  const confirmDelete = async () => {
    if (!repository) return;
    try {
      if (isBulkDelete) {
        await Promise.all(selectedUsers.map((u) => deleteUser(u.employeeId!)));
        setSelectedUsers([]);
      } else if (userToDelete) {
        await deleteUser(userToDelete.employeeId!);
        setSelectedUsers((prev) => prev.filter((u) => u.employeeId !== userToDelete.employeeId));
      }

      await queryClient.invalidateQueries({ queryKey: ['user'] });
      showSuccessMessage(toast, t('usersManagement.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setUserToDelete(null);
      setIsBulkDelete(false);
    }
  };

  const rejectDelete = () => {
    setUserToDelete(null);
    setIsBulkDelete(false);
  };

  const deleteSelectedUsers = () => {
    if (selectedUsers.length === 0) return;
    setIsBulkDelete(true);
  };

  return (
    <ContentCard>
      <HeaderTitle title={t('usersManagement.title')} />
      <TableHeader
        onCreate={handleShowCreateUser}
        onDelete={deleteSelectedUsers}
        onRefresh={async () => {
          setIsRefreshing(true);
          await queryClient.invalidateQueries({ queryKey: ['user'] });
          setIsRefreshing(false);
        }}
        onExportExcel={onExportExcel}
        onExportPdf={onExportPdf}
        visibility={toolbar}
        exportable
      />
      <AppLoading isLoading={loadingUsers || isRefreshing}>
        <AppTable
          headers={[
            { name: t('usersManagement.employeeId'), field: 'employeeId' },
            { name: t('usersManagement.name'), field: 'fullName' },
            { name: t('usersManagement.roles'), field: 'rolesDesc' },
          ]}
          data={usersData}
          onEdit={hasEditPermissions ? handleEditUser : undefined}
          onDelete={hasEditPermissions ? handleDeleteUser : undefined}
          onSelectedChange={
            hasEditPermissions
              ? (rows) => {
                  const filtered = rows.filter(
                    (r) => !isUserInSessionOrRoleSuperAdmin(r, currentUser),
                  );
                  setSelectedUsers(filtered);
                }
              : undefined
          }
          disableActions={(row) =>
            isUserInSessionOrRoleSuperAdmin({ employeeId: row.employeeId }, currentUser)
          }
        />
        <AppDialog
          visible={isModalVisible}
          title={
            isEditMode
              ? t('usersManagement.updateUser', {
                  employeeId: newEditUser.employeeId,
                })
              : t('usersManagement.createUser')
          }
          onClose={() => setIsModalVisible(false)}
          disableButtons={loadingUsers || loadingRoles}
          onConfirm={handleCreateEdit}
        >
          <AppLoading isLoading={loadingRoles}>
            <UserForm
              values={newEditUser}
              availableRoles={availableRoles}
              handleChange={(field, value) => setNewEditUser({ ...newEditUser, [field]: value })}
              handleRoleChange={handleRoleChange}
              errors={validationErrors}
              isEditMode={isEditMode}
            />
          </AppLoading>
        </AppDialog>
        <ConfirmDeleteDialog
          confirmDelete={confirmDelete}
          rejectDelete={rejectDelete}
          visible={isBulkDelete || userToDelete !== null}
          message={
            isBulkDelete
              ? t('usersManagement.confirmBulkDelete', {
                  employeeIds: formatList(
                    selectedUsers.map((u) => u.employeeId),
                    t('common.and'),
                  ),
                })
              : t('usersManagement.confirmDelete', {
                  employeeId: userToDelete?.employeeId,
                })
          }
        />
      </AppLoading>
    </ContentCard>
  );
};

export default UsersManagement;
